import React, { useMemo, useEffect } from 'react';
import { StackedBarChart } from '@/components/ui/chart-wrapper';
import DataTable from './DataTable';
import WidgetBase from './WidgetBase';
import { useWidgetData } from '../../hooks/useWidgetData';
import { exportToCSV, normalizeCell } from '../../utils/exportToCSV';
import { useChartColors } from '../../hooks/useChartColors';

/**
 * Multi-Stack Bar Widget Component
 * Modernized replacement for MultiStackBar_List.js
 * Displays stacked bar chart with multiple datasets
 */
function MultiStackBarWidget({ data, onLegendUpdate, onExportReady, onDataReady, mode = 'chart' }) {
    const colors = useChartColors();

    // Fetch data using custom hook
    const { data: apiData, loading, error } = useWidgetData(
        data.url,
        data.RequestJson,
        data.RequestJson1
    );

    // Colors matching old MultiStackBar_List.js:
    //   ds1 → chart1 token,  ds2 → data.ColorCode / chart2,  ds4 → chart2 (orange/bonus)
    const color1 = colors.chart1;
    const color2 = data.ColorCode || colors.chart2;
    const color3 = colors.chart2;

    // Transform API data to Chart.js stacked bar format
    // API returns 3 separate series: columnDataSet1[0], columnDataSet2[0], columnDataSet4[0]
    // Each has { toplabel, data, labels }
    const chartData = useMemo(() => {
        if (!apiData || apiData.length === 0) return null;

        try {
            const ds1 = apiData[0].columnDataSet1?.[0];
            const ds2 = apiData[0].columnDataSet2?.[0];
            const ds4 = apiData[0].columnDataSet4?.[0];

            if (!ds1 || !ds1.labels || !ds1.data || ds1.labels.length === 0 || ds1.data.length === 0) return null;

            const labels = ds1.labels;
            const datasets = [];

            // ds1 = Direct (chart1), ds2 = SubDist (ColorCode/chart2), ds4 = Bonus (chart2/chart5)
            datasets.push({
                label: ds1.toplabel || 'Series 1',
                data: ds1.data,
                backgroundColor: color1,
                borderColor: color1,
                stack: '1',
                hoverBackgroundColor: colors.chart1,
                borderWidth: 1
            });

            if (ds2 && ds2.data) {
                datasets.push({
                    label: ds2.toplabel || 'Series 2',
                    data: ds2.data,
                    backgroundColor: color2,
                    borderColor: color2,
                    stack: '1',
                    hoverBackgroundColor: colors.chart2,
                    borderWidth: 1
                });
            }

            if (ds4 && ds4.data) {
                datasets.push({
                    label: ds4.toplabel || 'Series 3',
                    data: ds4.data,
                    backgroundColor: color3,
                    borderColor: color3,
                    stack: '1',
                    hoverBackgroundColor: colors.chart5,
                    borderWidth: 1
                });
            }

            return { labels, datasets };
        } catch (err) {
            console.error('Error transforming stacked bar chart data:', err);
            return null;
        }
    }, [apiData, data, colors]);

    // Table data from listDataSet (same format as TableWidget)
    const { tableData, columns } = useMemo(() => {
        if (!apiData || apiData.length === 0) return { tableData: [], columns: [] };

        try {
            const listDataSet = apiData[0].listDataSet;
            if (!listDataSet || listDataSet.length === 0) return { tableData: [], columns: [] };

            // First row defines column headers (header text is in .data field)
            const cols = (listDataSet[0].datasets || []).map((ds, idx) => ({
                key: `col${idx}`,
                label: ds.data || `Col ${idx + 1}`,
                sortable: true,
                format: (val) => val ?? '-'
            }));

            // Data rows start at index 1 (index 0 is the header row)
            const rows = listDataSet.slice(1).map(item => {
                const row = {};
                (item.datasets || []).forEach((ds, idx) => {
                    row[`col${idx}`] = ds.data;
                });
                return row;
            });

            return { tableData: rows, columns: cols };
        } catch (err) {
            console.error('Error building stacked bar table data:', err);
            return { tableData: [], columns: [] };
        }
    }, [apiData]);

    // Extract 3-item legend and sum from API response (matches old MultiStackBar_List.js)
    const legendInfo = useMemo(() => {
        if (!apiData || apiData.length === 0) return null;
        try {
            return {
                text1: apiData[0].legend?.[0]?.legendText || '',
                value1: apiData[0].legend?.[0]?.legendValue || '',
                text2: apiData[0].legend?.[1]?.legendText || '',
                value2: apiData[0].legend?.[1]?.legendValue || '',
                text3: apiData[0].legend?.[2]?.legendText || '',
                value3: apiData[0].legend?.[2]?.legendValue || '',
                sum: apiData[0].sumOfLegendValues?.[0]?.sumOfLegendValue || ''
            };
        } catch { return null; }
    }, [apiData]);

    // Notify parent whether this widget has data (used to disable toggle / export buttons).
    // Use derived chartData/tableData — raw apiData can be non-empty with no real content.
    useEffect(() => {
        if (loading) return;
        if (onDataReady) onDataReady(chartData !== null || tableData.length > 0);
    }, [loading, chartData, tableData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Report legend (sumOfLegendValue) to parent for CardTitle display
    useEffect(() => {
        if (!apiData || apiData.length === 0 || !onLegendUpdate) return;
        try {
            const sumVal = apiData[0].sumOfLegendValues?.[0]?.sumOfLegendValue ?? '';
            if (sumVal) {
                // Commission/revenue widgets prefix with $
                const isMonetary = data.title?.toLowerCase().includes('commission') || data.title?.toLowerCase().includes('revenue');
                onLegendUpdate(data.id, isMonetary ? '$' + sumVal : String(sumVal));
            }
        } catch (e) { /* ignore */ }
    }, [apiData]);

    // Export handler — mirrors Bridge's ExportWidget which iterates listDataSet directly.
    // listDataSet[0] is the header row; subsequent rows are data.
    // Each row has datasets[j].data (a string). We strip embedded commas (thousands separators)
    // before writing to CSV, exactly as the Bridge branch does.
    const handleExport = () => {
        if (!apiData || apiData.length === 0) return;
        try {
            const listDataSet = apiData[0].listDataSet;
            if (!listDataSet || listDataSet.length === 0) return;

            const tableRows = listDataSet.map((row, rowIdx) =>
                (row.datasets || []).map((ds, colIdx) => {
                    // Row 0 is the header row — never alter headers.
                    // Column 0 on data rows is always the date column: unconditionally prefix
                    // with a space so all spreadsheet apps treat it as plain text.
                    if (rowIdx > 0 && colIdx === 0) {
                        return ' ' + String(ds.data ?? '').replace(/,/g, '');
                    }
                    return normalizeCell(ds.data);
                }).join(',')
            );

            const csvContent = tableRows.join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `${data.title || 'Total Revenue Earned'}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exporting MultiStackBar data:', err);
        }
    };

    // Register export handler with parent (for header-level export button)
    useEffect(() => {
        if (onExportReady) onExportReady(handleExport);
    }, [apiData]);

    return (
        <WidgetBase
            loading={loading}
            error={error}
            chartComponent={
                chartData ? (
                    <div>
                        {/* 3-item legend (matches old "transitions" section) */}
                        {legendInfo && (
                            <div className="flex flex-wrap justify-center gap-4 mb-3">
                                {legendInfo.text1 && (
                                    <div className="flex items-center gap-2">
                                        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: color1 }} />
                                        <span className="font-semibold text-sm">{legendInfo.value1}</span>
                                        <span className="text-sm text-muted-foreground">{legendInfo.text1}</span>
                                    </div>
                                )}
                                {legendInfo.text2 && (
                                    <div className="flex items-center gap-2">
                                        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: color2 }} />
                                        <span className="font-semibold text-sm">{legendInfo.value2}</span>
                                        <span className="text-sm text-muted-foreground">{legendInfo.text2}</span>
                                    </div>
                                )}
                                {legendInfo.text3 && (
                                    <div className="flex items-center gap-2">
                                        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: color3 }} />
                                        <span className="font-semibold text-sm">{legendInfo.value3}</span>
                                        <span className="text-sm text-muted-foreground">{legendInfo.text3}</span>
                                    </div>
                                )}
                            </div>
                        )}
                        <StackedBarChart
                            datasets={chartData.datasets}
                            labels={chartData.labels}
                            height={280}
                            options={{
                                scales: {
                                    x: {
                                        offset: true,
                                        title: {
                                            display: !!(data.XlabelString),
                                            text: data.XlabelString || ''
                                        },
                                        ticks: {
                                            display: false,
                                            autoSkip: true,
                                            maxTicksLimit: 5
                                        },
                                        grid: { display: false, drawBorder: false }
                                    },
                                    y: {
                                        title: {
                                            display: !!(data.YlabelString),
                                            text: data.YlabelString || ''
                                        },
                                        beginAtZero: true,
                                        grid: { drawBorder: false, display: false }
                                    }
                                },
                                plugins: {
                                    legend: { display: false },
                                    tooltip: { mode: 'index', intersect: false }
                                }
                            }}
                        />
                    </div>
                ) : null
            }
            tableComponent={
                tableData.length > 0 ? (
                    <DataTable
                        columns={columns}
                        data={tableData}
                        pagination={{ enabled: true, itemsPerPage: 6 }}
                        sortable={true}
                        searchable={false}
                    />
                ) : (
                    <div className="flex items-center justify-center py-12">
                        <p className="text-primary/70">No Data Available</p>
                    </div>
                )
            }
            mode={mode}
        />
    );
}

export default MultiStackBarWidget;
